import React from "react";
import { hydrateRoot } from "react-dom/client";
const mountOrRerender = (container, element) => {
    const host = container;
    if (host.__reactivatedRoot != null) {
        host.__reactivatedRoot.render(element);
    }
    else {
        host.__reactivatedRoot = hydrateRoot(container, element);
    }
};
/** The registered config; read by the SSR renderer after entry evaluation. */
export const getReactivateConfig = () => globalThis.__REACTIVATED_CONFIG__ ?? {};
/**
 * The one entry point. Call it from client/index.tsx (or let the virtual
 * default entry call it bare). During SSR the entry module is evaluated for
 * its side effect: reactivate() registers the config and returns. In the
 * browser it parses the preloaded server data, awaits init, resolves the
 * template, wraps it with render, and hydrates.
 */
export const reactivate = (config = {}) => {
    globalThis.__REACTIVATED_CONFIG__ = config;
    if (typeof window === "undefined") {
        return;
    }
    void (async () => {
        const props = window.__PRELOADED_PROPS__;
        const context = window.__PRELOADED_CONTEXT__;
        await config.init?.({ context, props });
        const { getTemplate } = await import("virtual:reactivated/templates");
        // @ts-ignore
        const { Provider } = await import("@reactivated");
        const Template = await getTemplate(context);
        const content = (React.createElement(React.StrictMode, null,
            React.createElement(Provider, { value: context },
                React.createElement(Template, { ...props }))));
        const wrapped = await (config.render ?? ((c) => c))(content, { ssr: false, context, props });
        if (config.mount != null) {
            await config.mount({ context, props, content: wrapped });
            return;
        }
        mountOrRerender(document, wrapped);
    })();
};
/**
 * Hydrate an SSR'd template fragment inside a page the framework does not
 * own — the Django admin being the canonical case. The base template marks
 * the fragment root with data-reactivated-root; absent that marker this is
 * a no-op, so calling it unconditionally from an admin entry is safe.
 */
export const reactivateAdmin = () => {
    if (typeof window === "undefined") {
        return;
    }
    const fragmentRoot = document.querySelector("[data-reactivated-root]");
    if (fragmentRoot == null) {
        return;
    }
    // Fragment SSR has no <head> to hoist into, so the stream emits the
    // entry's resource links (stylesheet, modulepreload) at its start and
    // they land inside the fragment container. A container is not a head
    // singleton: if hydration mismatches (e.g. iOS Safari's data detector
    // wrapping phone numbers in tel: anchors), React discards the
    // container's DOM wholesale and the stylesheet dies with it. Complete
    // the hoist React couldn't finish — adopt the links into document.head,
    // where the full-document path would have put them, so a mismatch
    // recovery degrades to a flash instead of an unstyled page. Moving a
    // live <link> neither refetches nor unapplies; hydration doesn't miss
    // the nodes because they were never part of the component tree.
    for (const link of Array.from(fragmentRoot.querySelectorAll("link"))) {
        document.head.appendChild(link);
    }
    void (async () => {
        const props = window.__PRELOADED_PROPS__;
        const context = window.__PRELOADED_CONTEXT__;
        const { getTemplate } = await import("virtual:reactivated/templates");
        // @ts-ignore
        const { Provider } = await import("@reactivated");
        const Template = await getTemplate(context);
        mountOrRerender(fragmentRoot, React.createElement(React.StrictMode, null,
            React.createElement(Provider, { value: context },
                React.createElement(Template, { ...props }))));
    })();
};
//# sourceMappingURL=client.js.map